// reducers/applicationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showError, showSuccess } from "../../helpers/toast";
import _ from "lodash";
import qs from "qs";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

API.interceptors.response.use(
  (response) => {
    return Promise.resolve(response);
  },
  (error) => {
    showError(error.response?.data?.error ?? error.message);
    return Promise.reject(error);
  }
);

const initialState = {
  fetchLoading: false,
  fetchError: null,
  message: null,
  data: [],
  application: {},
  lastFetchParams: null, // Track last query params to detect if we need to refetch
  totalCount: 0, // Total count from API for pagination
  totalPages: 1, // Total pages from API
  globalLoading: false, // Global loading state for actions
  applicationLoading: false, // Loading state for fetching single application
};

export const fetchApplications = createAsyncThunk(
  "fetchApplications",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };

      // PERFORMANCE: Default to false - only fetch all pages if explicitly requested
      // This prevents loading thousands of applications and crashing the app
      const fetchAllPages = data?.fetchAllPages === true;

      // Build base URL with query params
      let baseUrl = "/applications/";
      const baseQueryParams = { ...data?.queryParams };

      // PERFORMANCE: Preserve pagination params if provided, otherwise use defaults
      // Only remove pagination params if fetchAllPages is true
      if (fetchAllPages) {
        delete baseQueryParams.page;
        delete baseQueryParams.per_page;
        delete baseQueryParams.limit;
        delete baseQueryParams.page_size;
      } else {
        // Default pagination: first page, 50 items per page (reasonable limit)
        if (!baseQueryParams.page) baseQueryParams.page = 1;
        if (!baseQueryParams.per_page && !baseQueryParams.limit && !baseQueryParams.page_size) {
          baseQueryParams.per_page = 50; // Reasonable default instead of fetching all
        }
      }

      if (baseQueryParams && !_.isEmpty(baseQueryParams)) {
        baseUrl = `${baseUrl}?${baseQueryParams instanceof Object
          ? qs.stringify(baseQueryParams)
          : baseQueryParams
          }`;
      }

      // First, try to fetch with a high per_page limit to get all data in one request
      let firstPageData = null;
      let firstResponse = null;
      let usedHighLimit = false;

      // Only try high limit if fetchAllPages is true
      if (fetchAllPages) {
        const highLimitUrl = baseUrl.includes('?')
          ? `${baseUrl}&page=1&per_page=1000`
          : `${baseUrl}?page=1&per_page=1000`;

        try {
          firstResponse = await API.get(highLimitUrl, config);
          firstPageData = firstResponse.data;
          usedHighLimit = true;

          // Ensure response has applications array
          if (Array.isArray(firstPageData)) {
            firstPageData = { applications: firstPageData, total: firstPageData.length };
          }

          // If we got all the data in one request, return it
          if (firstPageData.applications &&
            (!firstPageData.totalPages || firstPageData.totalPages <= 1 ||
              firstPageData.applications.length >= (firstPageData.total || 0))) {
            return firstPageData;
          }
          // If per_page worked but didn't return all data, we'll use this response and continue with pagination
        } catch (highLimitError) {
          // If per_page parameter doesn't work, continue with pagination approach
        }
      }

      // If we didn't use high limit or it failed, fetch with the URL we built (includes pagination if fetchAllPages is false)
      if (!usedHighLimit) {
        try {
          firstResponse = await API.get(baseUrl, config);
          firstPageData = firstResponse.data;

          // If fetchAllPages is false, return the paginated response directly
          if (!fetchAllPages) {
            // Ensure response has applications array (handle different API response structures)
            if (Array.isArray(firstPageData)) {
              return { applications: firstPageData, total: firstPageData.length };
            } else if (firstPageData.applications) {
              return firstPageData;
            } else {
              // Fallback: wrap in expected structure
              return { applications: [], total: 0 };
            }
          }
        } catch (pageError) {
          // If request fails, try without pagination params as fallback
          console.warn("⚠️ Request failed, trying without pagination params:", pageError);
          try {
            const fallbackUrl = "/applications/";
            firstResponse = await API.get(fallbackUrl, config);
            firstPageData = firstResponse.data;
            // Handle different response structures
            if (Array.isArray(firstPageData)) {
              return { applications: firstPageData, total: firstPageData.length };
            } else if (firstPageData.applications) {
              return firstPageData;
            }
            return { applications: [], total: 0 };
          } catch (fallbackError) {
            console.error("Failed to fetch applications:", fallbackError);
            throw new Error("Failed to load applications");
          }
        }
      }

      // If fetchAllPages is false and we used high limit, return the first page data
      if (!fetchAllPages && usedHighLimit) {
        // Ensure response has applications array
        if (Array.isArray(firstPageData)) {
          return { applications: firstPageData, total: firstPageData.length };
        } else if (firstPageData.applications) {
          return firstPageData;
        }
        return { applications: [], total: 0 };
      }

      // If there's only one page or no totalPages info, return first page data
      if (!firstPageData.totalPages || firstPageData.totalPages <= 1) {
        return firstPageData;
      }

      // Fetch all remaining pages
      const allApplications = [...(firstPageData.applications || [])];
      const totalPages = firstPageData.totalPages || 1;

      // PERFORMANCE: Limit to reasonable number of pages to avoid crashes
      // Reduced from 50 to 10 pages max (100 items with per_page=10, or 500 with per_page=50)
      const maxPagesToFetch = Math.min(totalPages, 10);

      // Fetch remaining pages in parallel (with error handling)
      const pagePromises = [];
      for (let page = 2; page <= maxPagesToFetch; page++) {
        const pageUrl = baseUrl.includes('?')
          ? `${baseUrl}&page=${page}`
          : `${baseUrl}?page=${page}`;
        // Wrap in a promise that handles errors gracefully
        pagePromises.push(
          API.get(pageUrl, config).catch((error) => {
            console.warn(`Failed to fetch page ${page}:`, error);
            return { data: { applications: [] } }; // Return empty array on error
          })
        );
      }

      const remainingResponses = await Promise.all(pagePromises);
      remainingResponses.forEach((response) => {
        if (response?.data?.applications) {
          allApplications.push(...response.data.applications);
        }
      });

      // Return combined data with same structure as original response
      return {
        ...firstPageData,
        applications: allApplications,
        total: allApplications.length,
      };
    } catch (error) {
      throw new Error("Failed to load applications");
    }
  },
  {
    condition: (data, { getState }) => {
      // Prevent duplicate requests only if:
      // 1. A request is already in progress
      // 2. AND the query params are the same
      // 3. AND we already have data
      const state = getState();
      const currentParams = JSON.stringify(data?.queryParams || {});
      const lastParams = state.applications.lastFetchParams;
      const hasData = state.applications.data && state.applications.data.length > 0;

      // If already loading with same params AND we have data, skip
      if (state.applications.fetchLoading && currentParams === lastParams && hasData) {
        return false; // Skip this request
      }

      // Always allow request if:
      // - Not loading, OR
      // - Different query params (need different data), OR
      // - No data yet (need to fetch initial data)
      if (!hasData) {
      } else if (currentParams !== lastParams) {
      }
      return true; // Allow the request
    }
  }
);

export const fetchApplication = createAsyncThunk(
  "fetchApplication",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `Bearer ${data.accessToken}`,
        },
      };

      // Validate ID
      if (!data.id || data.id === 'undefined' || data.id === 'null') {
        throw new Error('Invalid application ID');
      }
      // Try different endpoint patterns
      let response;
      try {
        response = await API.get("/applications/" + data?.id, config);
      } catch (error) {
        try {
          response = await API.get("/applications/" + data?.id, config);
        } catch (error2) {
          response = await API.get("/applications/" + data?.id, config);
        }
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch application:', error);
      throw new Error("Failed to load application");
    }
  }
);

export const getApplication = async (id, authToken) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}applications/${id}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to get application");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to get application :", error);
  }
};

export const postApplication = createAsyncThunk(
  "postApplication",
  async (data) => {
    try {
      const formData = new FormData();
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${data.token}`,
        },
      };
      formData.append("form", data?.form);
      formData.append("json_data", JSON.stringify(data?.json_data));
      formData.append("name", data?.name);
      formData.append("category", data?.category);
      formData.append("user_id", data?.user_id);
      formData.append("state", data?.state);
      
      // Add sourceApplicationId if provided (for copying files from another application)
      if (data?.sourceApplicationId) {
        formData.append("sourceApplicationId", data.sourceApplicationId);
      }
      
      // Add sourceUserId if provided
      if (data?.sourceUserId) {
        formData.append("sourceUserId", data.sourceUserId);
      }
      
      if (data?.files && data.files.length > 0) {
        // Separate existing files (file names) from new files (File objects)
        const fileNames = data.files
          .filter((file) => !file?.type && file?.name) // Files without type property (file names)
          .map((file) => file.name);

        // Append file names as previousFiles (matching saveRenewalApplication pattern)
        // This tells the backend which files to copy from the source application
        if (fileNames.length > 0) {
          formData.append("previousFiles", JSON.stringify(fileNames));
        }

        // Append new files (File objects with type property)
        data.files.forEach((file) => {
          if (file?.type) {
            formData.append(`files`, file);
          }
        });
      } else {
      }
      
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
        } else {
        }
      }

      // Based on the error, let's try the most common patterns
      // First, let's try without the v2 version
      let response;
      try {
        response = await API.post("/applications", formData, config);
      } catch (error) {
        try {
          response = await API.post("/applications", formData, config);
        } catch (error2) {
          try {
            response = await API.post("/applications/", formData, config);
          } catch (error3) {
            response = await API.post("/applications/", formData, config);
          }
        }
      }

      showSuccess("Create Successfully!");
      return response.data;
    } catch (error) {
      console.error('Error creating application:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      throw new Error("Failed to create application: " + (error.response?.data?.message || error.message));
    }
  }
);

export const updateApplication = createAsyncThunk(
  "updateApplication",
  async (data) => {
    try {

      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `Bearer ${data.token}`,
        },
      };

      const payload = {};
      if (data?.state) payload.state = data.state;
      if (data?.json_data) payload.json_data = JSON.stringify(data?.json_data);
      if (data?.dueDate) payload.dueDate = data.dueDate;
      if (data?.descripencie) payload.descripencie = data.descripencie;
      if (data?.htmlForm) payload.htmlForm = data?.htmlForm;

      const response = await API.put(
        `/applications/${data?.id}`,
        payload,
        config
      );

      showSuccess("Your application has been updated!");
      return response.data;
    } catch (error) {
      console.error('updateApplication error:', error);
      throw new Error("Failed to update application");
    }
  }
);

export const getFormMandatoryFields = async (id, authToken) => {
  try {
    // Try the forms endpoint first to get form details
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}forms/${id}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      console.warn(`Form ${id} not found, trying alternative endpoint...`);
      // Fallback: return empty mandatory fields if form not found
      return [{ mandatory_fields: [] }];
    }
    const data = await response.json();

    // If the form has mandatory_fields property, return it
    if (data.mandatory_fields) {
      return [{ mandatory_fields: data.mandatory_fields }];
    }

    // If no mandatory_fields property, return empty array
    return [{ mandatory_fields: [] }];
  } catch (error) {
    console.error("Failed to fetch form fields:", error);
    // Return empty mandatory fields on error
    return [{ mandatory_fields: [] }];
  }
};

export const safePdf = createAsyncThunk("safePdf", async (data) => {
  try {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        "Content-Type": "application/json",
        Authorization: `${data.token}`,
      },
    };

    const payload = {};

    if (data?.htmlForm) payload.htmlForm = data?.htmlForm;
    if (data?.user) payload.user = data?.user;

    const response = await API.post(
      `/applications/${data?.id}/savepdf`,
      payload,
      config
    );
    showSuccess("Your Pdf is saved!");
    return response.data;
  } catch (error) {
    throw new Error("Failed to load applications");
  }
});

export const uploadFile = createAsyncThunk("uploadFile", async (data) => {
  const { accessToken, payload } = data;

  try {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        "Content-Type": "multipart/form-data",
        Authorization: `${accessToken}`,
      },
    };
    const response = await API.post(
      "/applications/upload-file",
      payload,
      config
    );

    return response.data;
  } catch (error) {
    throw new Error("Failed to upload file");
  }
});

export const updateApplicationData = createAsyncThunk(
  "updateApplicationData",
  async (data) => {
    try {

      const formData = new FormData();
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${data.token}`,
        },
      };

      // formData.append("form", data?.form);
      formData.append("json_data", JSON.stringify(data?.json_data));
      formData.append("state", data?.state);
      formData.append("userId", data.userId);
      if (data?.files && data.files.length > 0) {
        // Separate existing files (without type property) from new files (with type property)
        const previousFiles = data.files
          .filter((file) => !file?.type && file?.name) // Files without type property (existing files)
          .map((file) => file.name);

        // Append previousFiles as JSON string if there are any
        if (previousFiles.length > 0) {
          formData.append("previousFiles", JSON.stringify(previousFiles));
        }

        // Append new files (File objects with type property)
        data.files.forEach((file) => {
          if (file?.type) {
            formData.append(`files`, file);
          }
        });
      }

      for (let [key, value] of formData.entries()) {
      }

      const response = await API.put(
        `/applications/${data?.id}/updateApplication`,
        formData,
        config
      );

      return response.data;
    } catch (error) {
      console.error('updateApplicationData error:', error);
      throw new Error("Failed to update application data");
    }
  }
);

export const getUserCertificates = async (data) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}applications/${data?.userId}/certificates`,
      {
        method: "GET",
        headers: {
          Authorization: `${data.authToken}`,
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error("Error Getting data:", error);
  }
};

export const fetchV2Applications = async (data) => {
  try {
    const { accessToken, ...queryParams } = data;

    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(
        ([, value]) => value !== undefined && value !== null
      )
    );

    const queryString = new URLSearchParams(filteredParams).toString();
    const url = queryString
      ? `${process.env.REACT_APP_BACKEND_URL}applications?${queryString}`
      : `${process.env.REACT_APP_BACKEND_URL}applications`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to load applications");
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error fetching applications:", error);
    throw new Error("Failed to load applications");
  }
};

export const saveRenewalApplication = async (data) => {
  try {
    const formData = new FormData();
    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${data.token}`,
      },
    };
    formData.append("json_data", JSON.stringify(data?.json_data));
    formData.append("state", data?.state);
    formData.append("userId", data.userId);
    
    if (data?.files && data.files.length > 0) {
      // Separate existing files (without type property) from new files (with type property)
      const previousFiles = data.files
        .filter((file) => !file?.type && file?.name) // Files without type property (existing files)
        .map((file) => file.name);

      // Append previousFiles as JSON string if there are any
      if (previousFiles.length > 0) {
        formData.append("previousFiles", JSON.stringify(previousFiles));
      }

      // Append new files (File objects with type property)
      data.files.forEach((file) => {
        if (file?.type) {
          formData.append(`files`, file);
        }
      });
    }

    const response = await API.post(
      `/applications/${data?.id}/renewal`,
      formData,
      config
    );
    if (response.status !== 200) {
      throw new Error("Failed to save application");
    }

    return response.data;
  } catch (error) {
    throw new Error("Failed to load applications");
  }
};

const applicationSlice = createSlice({
  name: "applications",
  initialState,
  reducers: {
    setFetchLoading: (state, action) => {
      state.fetchLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplications.pending, (state, action) => {
        state.fetchLoading = true;
        // Store the query params being used for this request
        const meta = action.meta;
        if (meta?.arg?.queryParams) {
          state.lastFetchParams = JSON.stringify(meta.arg.queryParams);
        } else {
          state.lastFetchParams = JSON.stringify({});
        }
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.data = action.payload.applications || action.payload.data || [];
        // Store pagination metadata from API response for proper pagination UI
        state.totalCount = action.payload.total || action.payload.totalCount || state.data.length;
        state.totalPages = action.payload.totalPages || Math.ceil(state.totalCount / (action.payload.per_page || 50)) || 1;
        state.fetchError = null;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.error.message;
        state.data = null;
        state.message = null;
      })

      .addCase(fetchApplication.pending, (state) => {
        state.applicationLoading = true;
        state.fetchLoading = true;
      })
      .addCase(fetchApplication.fulfilled, (state, action) => {
        state.applicationLoading = false;
        state.fetchLoading = false;
        // Handle different possible response structures
        state.application = action.payload.applications || action.payload.application || action.payload;
        state.fetchError = null;
      })
      .addCase(fetchApplication.rejected, (state, action) => {
        state.applicationLoading = false;
        state.fetchLoading = false;
        state.fetchError = action.error.message;
        state.application = null;
      })
      .addCase(updateApplicationData.pending, (state) => {
        state.globalLoading = true;
      })
      .addCase(updateApplicationData.fulfilled, (state) => {
        state.globalLoading = false;
      })
      .addCase(updateApplicationData.rejected, (state) => {
        state.globalLoading = false;
      })
      .addCase(updateApplication.pending, (state) => {
        state.globalLoading = true;
      })
      .addCase(updateApplication.fulfilled, (state) => {
        state.globalLoading = false;
      })
      .addCase(updateApplication.rejected, (state) => {
        state.globalLoading = false;
      })
      .addCase(safePdf.pending, (state) => {
        state.globalLoading = true;
      })
      .addCase(safePdf.fulfilled, (state) => {
        state.globalLoading = false;
      })
      .addCase(safePdf.rejected, (state) => {
        state.globalLoading = false;
      });
  },
});

export const { setFetchLoading } = applicationSlice.actions;
export default applicationSlice.reducer;
